import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const axiosInstance = axios.create({ withCredentials: true });
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [filterPlan, setFilterPlan] = useState('all');
  const [passwordResets, setPasswordResets] = useState([]);
  const [showPasswordResets, setShowPasswordResets] = useState(false);
  const [surveySettings, setSurveySettings] = useState({ hasty_question_threshold: 1.0, idle_question_threshold: 200.0, hasty_survey_threshold: 2.0 });
  const [showSettingsSection, setShowSettingsSection] = useState(false);
  const [aiUsageData, setAiUsageData] = useState(null);
  const [showAiUsage, setShowAiUsage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionUser, setSubscriptionUser] = useState(null);
  const [newSubscriptionStatus, setNewSubscriptionStatus] = useState('sergeant');

  useEffect(() => {
    loadDashboardData();
    loadPasswordResets();
    loadSurveySettings();
    loadAiUsageStats();
  }, []);

  const loadSurveySettings = async () => {
    try { const response = await axiosInstance.get(`${API}/admin/survey-settings`); setSurveySettings(response.data); } catch (error) { console.error('Failed to load survey settings:', error); }
  };

  const updateSurveySettings = async () => {
    try { await axiosInstance.put(`${API}/admin/survey-settings`, surveySettings); toast.success('Survey settings updated successfully'); } catch (error) { toast.error('Failed to update survey settings'); }
  };

  const loadAiUsageStats = async () => {
    try { const response = await axiosInstance.get(`${API}/admin/ai-usage`); setAiUsageData(response.data); } catch (error) { console.error('Failed to load AI usage stats:', error); }
  };

  const loadDashboardData = async () => {
    try {
      const response = await axiosInstance.get(`${API}/admin/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      if (error.response?.status === 403) { toast.error('Admin access required'); navigate('/dashboard'); } else { toast.error('Failed to load admin dashboard'); }
    } finally { setLoading(false); }
  };

  const loadPasswordResets = async () => {
    try { const response = await axiosInstance.get(`${API}/admin/password-resets`); setPasswordResets(response.data); } catch (error) { console.error('Failed to load password resets:', error); }
  };

  const toggleAdminStatus = async (userId) => {
    try { await axiosInstance.post(`${API}/admin/toggle-admin/${userId}`); toast.success('Admin status updated'); await loadDashboardData(); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update admin status'); }
  };

  const toggleEmailVerified = async (userId, currentStatus) => {
    try { await axiosInstance.post(`${API}/admin/toggle-email-verified/${userId}`); toast.success(`Email verification ${currentStatus ? 'removed' : 'granted'}`); await loadDashboardData(); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update verification status'); }
  };

  const handleDeleteUserClick = (user) => { setUserToDelete(user); setShowDeleteConfirm(true); };

  const handleDeleteUserConfirm = async () => {
    if (!userToDelete) return;
    try { await axiosInstance.delete(`${API}/admin/delete-user/${userToDelete.id}`); toast.success(`User ${userToDelete.email} deleted successfully`); setShowDeleteConfirm(false); setUserToDelete(null); await loadDashboardData(); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete user'); }
  };

  const handleDeleteUserCancel = () => { setShowDeleteConfirm(false); setUserToDelete(null); };

  const handleSetSubscriptionClick = (user) => {
    setSubscriptionUser(user);
    setNewSubscriptionStatus(user.subscription_status || 'sergeant');
    setShowSubscriptionModal(true);
  };

  const handleSetSubscriptionConfirm = async () => {
    if (!subscriptionUser) return;
    try {
      await axiosInstance.post(`${API}/admin/set-subscription/${subscriptionUser.id}`, { subscription_status: newSubscriptionStatus });
      toast.success(`Updated ${subscriptionUser.email} to ${newSubscriptionStatus}`);
      setShowSubscriptionModal(false);
      setSubscriptionUser(null);
      await loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  if (loading) return <div className="loading-spinner">Loading admin dashboard...</div>;

  const overview = dashboardData?.overview || {};
  const users = dashboardData?.users || [];
  const filteredUsers = filterPlan === 'all' ? users : users.filter(u => u.subscription_status === filterPlan);

  return (
    <div className="dashboard-container admin-dashboard">
      <Toaster position="top-right" richColors />
      <div className="dashboard-header">
        <div className="logo"><span className="brand-cx">CX</span><span className="brand-slash">/</span><span className="brand-recon">recon</span><span className="admin-badge">ADMIN</span></div>
        <button className="back-btn" onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
      </div>

      <div className="admin-content">
        <h1 className="dashboard-title">Admin Dashboard</h1>

        <div className="admin-stats-grid">
          <div className="stat-card"><div className="stat-icon">👥</div><div className="stat-value">{overview.total_users}</div><div className="stat-label">Total Users</div></div>
          <div className="stat-card"><div className="stat-icon">💼</div><div className="stat-value">{(overview.monthly_users || 0) + (overview.yearly_users || 0)}</div><div className="stat-label">Paid Subscribers</div></div>
          <div className="stat-card"><div className="stat-icon">📋</div><div className="stat-value">{overview.total_surveys}</div><div className="stat-label">Total Surveys</div><div className="stat-sublabel">{overview.avg_surveys_per_user} avg per user</div></div>
          <div className="stat-card"><div className="stat-icon">💬</div><div className="stat-value">{overview.total_responses}</div><div className="stat-label">Total Responses</div><div className="stat-sublabel">{overview.avg_responses_per_survey} avg per survey</div></div>
          <div className="stat-card highlight"><div className="stat-icon">💰</div><div className="stat-value">${overview.total_revenue}</div><div className="stat-label">Total Revenue</div><div className="stat-sublabel">MRR: ${overview.monthly_recurring_revenue}</div></div>
        </div>

        <div className="plan-breakdown">
          <h2>User Distribution</h2>
          <div className="plan-stats">
            <div className="plan-stat"><span className="plan-name">Free</span><span className="plan-count">{overview.free_users}</span></div>
            <div className="plan-stat"><span className="plan-name">Monthly</span><span className="plan-count">{overview.monthly_users}</span></div>
            <div className="plan-stat"><span className="plan-name">Yearly</span><span className="plan-count">{overview.yearly_users}</span></div>
          </div>
        </div>

        <div className="users-section">
          <div className="section-header">
            <h2>All Users</h2>
            <div className="filter-controls">
              <select value={filterPlan} onChange={(e) => setFilterPlan(e.target.value)} className="plan-filter">
                <option value="all">All Plans</option><option value="free">Free</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option>
              </select>
            </div>
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr><th>User</th><th>Plan</th><th>Verified</th><th>Surveys</th><th>Responses</th><th>AI Usage</th><th>Taker AI</th><th>Days Left</th><th>Joined</th><th>Admin</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td><div className="user-info"><div className="user-name">{user.name}</div><div className="user-email">{user.email}</div></div></td>
                    <td>
                      <button 
                        className={`plan-badge clickable ${user.subscription_status}`}
                        onClick={() => handleSetSubscriptionClick(user)}
                        title="Click to change subscription"
                        data-testid={`subscription-badge-${user.id}`}
                      >
                        {user.subscription_status}
                      </button>
                    </td>
                    <td>
                      <button className={`verify-toggle-btn ${user.email_verified ? 'verified' : 'unverified'}`} onClick={() => toggleEmailVerified(user.id, user.email_verified)}
                        title={user.email_verified ? 'Click to revoke verification' : 'Click to verify email'} data-testid={`verify-toggle-${user.id}`}>
                        {user.email_verified ? '✓ Verified' : '✗ Unverified'}
                      </button>
                    </td>
                    <td>{user.survey_count}</td>
                    <td>{user.response_count}</td>
                    <td><span className="ai-usage-badge">🤖 {user.ai_usage_count || 0}</span></td>
                    <td><span className="survey-taker-ai-badge">✍️ {user.survey_taker_ai_count || 0}</span></td>
                    <td>{user.days_remaining !== null ? <span className={user.days_remaining <= 3 ? 'expiring-soon' : ''}>{user.days_remaining} days</span> : <span className="no-expiry">-</span>}</td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td><button className={`admin-toggle-btn ${user.is_admin ? 'active' : ''}`} onClick={() => toggleAdminStatus(user.id)} disabled={user.is_admin && filteredUsers.filter(u => u.is_admin).length === 1}>{user.is_admin ? '✓ Admin' : 'Make Admin'}</button></td>
                    <td><button className="delete-user-btn" onClick={() => handleDeleteUserClick(user)} title="Delete user and all associated data">🗑️ Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="password-resets-section">
          <div className="section-header">
            <h2>Password Reset Requests</h2>
            <button className="toggle-section-btn" onClick={() => setShowPasswordResets(!showPasswordResets)}>{showPasswordResets ? 'Hide' : `Show (${passwordResets.filter(r => !r.used).length} pending)`}</button>
          </div>
          {showPasswordResets && (
            <div className="password-resets-table-container">
              {passwordResets.length === 0 ? <p className="no-data">No password reset requests yet</p> : (
                <table className="password-resets-table">
                  <thead><tr><th>User Email</th><th>Reset Token</th><th>Status</th><th>Created</th><th>Expires</th></tr></thead>
                  <tbody>
                    {passwordResets.map((reset, index) => (
                      <tr key={index} className={reset.used ? 'used-reset' : ''}>
                        <td>{reset.user_email}</td>
                        <td><code className="reset-token">{reset.reset_token}</code>
                          <button className="copy-btn" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/reset-password?token=${reset.reset_token}`); toast.success('Reset link copied!'); }} title="Copy reset link">📋</button>
                        </td>
                        <td><span className={`status-badge ${reset.used ? 'used' : 'pending'}`}>{reset.used ? 'Used' : 'Pending'}</span></td>
                        <td>{new Date(reset.created_at).toLocaleString()}</td>
                        <td>{new Date(reset.expires_at) > new Date() ? <span>{new Date(reset.expires_at).toLocaleString()}</span> : <span className="expired">Expired</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        <div className="survey-settings-section">
          <div className="section-header">
            <h2>Survey Response Quality Settings</h2>
            <button className="toggle-section-btn" onClick={() => setShowSettingsSection(!showSettingsSection)}>{showSettingsSection ? 'Hide' : 'Show Configuration'}</button>
          </div>
          {showSettingsSection && (
            <div className="settings-config-container">
              <p className="settings-description">Configure the thresholds used to flag potentially fraudulent or low-quality survey responses.</p>
              <div className="settings-grid">
                <div className="setting-item">
                  <label htmlFor="hasty-question">Hasty Question Threshold (seconds)</label>
                  <p className="setting-help">Questions answered faster than this will be flagged</p>
                  <input id="hasty-question" type="number" step="0.1" min="0.1" value={surveySettings.hasty_question_threshold} onChange={(e) => setSurveySettings({...surveySettings, hasty_question_threshold: parseFloat(e.target.value) || 0.1})} className="setting-input" />
                </div>
                <div className="setting-item">
                  <label htmlFor="idle-question">Idle Question Threshold (seconds)</label>
                  <p className="setting-help">Questions taking longer than this will be flagged as idle</p>
                  <input id="idle-question" type="number" step="1" min="1" value={surveySettings.idle_question_threshold} onChange={(e) => setSurveySettings({...surveySettings, idle_question_threshold: parseFloat(e.target.value) || 1})} className="setting-input" />
                </div>
                <div className="setting-item">
                  <label htmlFor="hasty-survey">Hasty Survey Threshold (seconds per question)</label>
                  <p className="setting-help">Surveys averaging less than this per question will be flagged</p>
                  <input id="hasty-survey" type="number" step="0.1" min="0.1" value={surveySettings.hasty_survey_threshold} onChange={(e) => setSurveySettings({...surveySettings, hasty_survey_threshold: parseFloat(e.target.value) || 0.1})} className="setting-input" />
                </div>
              </div>
              <button className="save-settings-btn" onClick={updateSurveySettings}>Save Settings</button>
            </div>
          )}
        </div>

        <div className="ai-usage-section">
          <div className="section-header">
            <h2>🤖 AI Credit Usage Tracking</h2>
            <button className="toggle-section-btn" onClick={() => setShowAiUsage(!showAiUsage)}>{showAiUsage ? 'Hide' : 'Show'}</button>
          </div>
          {showAiUsage && aiUsageData && (
            <div className="ai-usage-content">
              <div className="ai-usage-summary">
                <div className="usage-stat-card"><div className="usage-stat-number">{aiUsageData.total_usage_count}</div><div className="usage-stat-label">Total AI Calls</div></div>
                <div className="usage-stat-card"><div className="usage-stat-number">{aiUsageData.last_30_days_count}</div><div className="usage-stat-label">Last 30 Days</div></div>
                <div className="usage-stat-card"><div className="usage-stat-number">{aiUsageData.usage_by_role.admin}</div><div className="usage-stat-label">Admin Usage</div></div>
                <div className="usage-stat-card"><div className="usage-stat-number">{aiUsageData.usage_by_role.survey_taker}</div><div className="usage-stat-label">Survey Taker Usage</div></div>
              </div>
              <div className="usage-breakdown">
                <h3>Usage Breakdown by Type</h3>
                <div className="usage-type-grid">
                  <div className="usage-type-item"><span className="usage-type-label">📝 Survey Creation</span><span className="usage-type-count">{aiUsageData.usage_by_type.survey_creation}</span></div>
                  <div className="usage-type-item"><span className="usage-type-label">📊 AI Summary Reports</span><span className="usage-type-count">{aiUsageData.usage_by_type.ai_summary_report}</span></div>
                  <div className="usage-type-item"><span className="usage-type-label">📈 Single Survey Summaries</span><span className="usage-type-count">{aiUsageData.usage_by_type.ai_summary_single}</span></div>
                  <div className="usage-type-item"><span className="usage-type-label">✍️ Text Rewrites (Survey Takers)</span><span className="usage-type-count">{aiUsageData.usage_by_type.text_rewrite}</span></div>
                </div>
              </div>
              {aiUsageData.recent_usage && aiUsageData.recent_usage.length > 0 && (
                <div className="recent-usage-section">
                  <h3>Recent AI Usage (Last 100)</h3>
                  <div className="recent-usage-table-container">
                    <table className="recent-usage-table">
                      <thead><tr><th>Timestamp</th><th>Type</th><th>User Role</th><th>User ID</th><th>Survey ID</th><th>Model</th></tr></thead>
                      <tbody>
                        {aiUsageData.recent_usage.slice(0, 20).map((record, index) => (
                          <tr key={index}>
                            <td>{new Date(record.timestamp).toLocaleString()}</td>
                            <td>{record.usage_type === 'survey_creation' && '📝 Survey Creation'}{record.usage_type === 'ai_summary_report' && '📊 Summary Report'}{record.usage_type === 'ai_summary_single' && '📈 Survey Summary'}{record.usage_type === 'text_rewrite' && '✍️ Text Rewrite'}</td>
                            <td><span className={`role-badge ${record.user_role}`}>{record.user_role}</span></td>
                            <td>{record.user_id === 'anonymous' ? 'Anonymous' : record.user_id.substring(0, 8)}...</td>
                            <td>{record.survey_id ? record.survey_id.substring(0, 8) + '...' : '-'}</td>
                            <td className="model-name">{record.model || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="usage-note">Showing 20 most recent records. Total in last 30 days: {aiUsageData.last_30_days_count}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {showDeleteConfirm && userToDelete && (
          <div className="modal-overlay" onClick={handleDeleteUserCancel}>
            <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="delete-modal-header"><h2>⚠️ Delete User Account</h2><button className="modal-close" onClick={handleDeleteUserCancel}>✕</button></div>
              <div className="delete-modal-content">
                <p className="warning-text">You are about to permanently delete this user account:</p>
                <div className="user-delete-info">
                  <div><strong>Name:</strong> {userToDelete.name}</div>
                  <div><strong>Email:</strong> {userToDelete.email}</div>
                  <div><strong>Surveys:</strong> {userToDelete.survey_count} surveys will be deleted</div>
                  <div><strong>Responses:</strong> {userToDelete.response_count} responses will be lost</div>
                </div>
                <p className="warning-text danger">⚠️ This action cannot be undone! All user data, surveys, and associated information will be permanently deleted.</p>
                <div className="delete-modal-actions">
                  <button className="cancel-delete-btn" onClick={handleDeleteUserCancel}>Cancel</button>
                  <button className="confirm-delete-btn" onClick={handleDeleteUserConfirm}>Yes, Delete User</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Change Modal */}
        {showSubscriptionModal && subscriptionUser && (
          <div className="modal-overlay" onClick={() => setShowSubscriptionModal(false)}>
            <div className="subscription-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>💳 Change Subscription</h2>
                <button className="modal-close" onClick={() => setShowSubscriptionModal(false)}>✕</button>
              </div>
              <div className="modal-body">
                <p className="user-info-line"><strong>User:</strong> {subscriptionUser.name}</p>
                <p className="user-info-line"><strong>Email:</strong> {subscriptionUser.email}</p>
                <p className="user-info-line"><strong>Current Plan:</strong> <span className={`plan-badge ${subscriptionUser.subscription_status}`}>{subscriptionUser.subscription_status}</span></p>
                
                <div className="form-group" style={{ marginTop: '1.5rem' }}>
                  <label className="form-label">New Subscription Status:</label>
                  <select 
                    className="form-input"
                    value={newSubscriptionStatus}
                    onChange={(e) => setNewSubscriptionStatus(e.target.value)}
                    data-testid="subscription-select"
                  >
                    <option value="free">Free (Recruit)</option>
                    <option value="recruit">Recruit</option>
                    <option value="sergeant">Sergeant (Monthly)</option>
                    <option value="lieutenant">Lieutenant (Yearly)</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                
                <div className="modal-actions" style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button className="cancel-btn" onClick={() => setShowSubscriptionModal(false)}>Cancel</button>
                  <button className="confirm-btn" onClick={handleSetSubscriptionConfirm} data-testid="confirm-subscription-btn">Update Subscription</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
